import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import SideNavbar from '../../components/Navbar/SideNavbar';
import Header from '../../components/Header/Header';
import { Form, Input, InputNumber, Switch, DatePicker, Select, Button, Card, Space, Divider, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import {
    createShippingCriteriaService,
    updateShippingCriteriaService,
    getShippingRuleDetailService,
    getCategoryLevelOneService,
    ShippingRulePayload,
    RuleCriteria
} from '../../../core/services/shippingService';

interface Props {
    mode?: 'create' | 'update';
}

const ShippingRuleFormPage: React.FC<Props> = ({ mode = 'create' }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const isUpdate = mode === 'update' || !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});

    // Form states
    const [priority, setPriority] = useState<number>(1);
    const [active, setActive] = useState<boolean>(true);
    const [dateActive, setDateActive] = useState<moment.Moment | null>(moment());
    const [dateExpiry, setDateExpiry] = useState<moment.Moment | null>(moment().add(1, 'year'));

    const [ruleCriteriaList, setRuleCriteriaList] = useState<RuleCriteria[]>([
        {
            priority: 1,
            evaluationType: 'ANY_OF',
            ruleList: [
                {
                    conditionsList: [
                        {
                            evaluationType: 'ANY_OF',
                            criteriaList: [
                                {
                                    categoryType: 'CATEGORY',
                                    attributeList: [{ categoryType: 'CATEGORY', attributeType: 'CATEGORY_ID', attributeValues: [''] }]
                                }
                            ]
                        }
                    ],
                    actionList: {
                        criteriaList: [
                            {
                                categoryType: 'SHIPPING',
                                attributeList: [{ categoryType: 'SHIPPING', attributeType: 'SPECIAL_PRODUCT_SHIPPING_CHARGE', attributeValues: ['0'] }]
                            }
                        ]
                    }
                }
            ]
        }
    ]);

    useEffect(() => {
        fetchLevelOneCategories();
        if (isUpdate && id) {
            loadRuleDetail(id);
        }
    }, [id]);

    const fetchLevelOneCategories = async () => {
        try {
            const res = await getCategoryLevelOneService();
            if (res?.data?.data) {
                const map: Record<string, string> = {};
                let options: { label: string; value: string }[] = [];

                if (Array.isArray(res.data.data)) {
                    res.data.data.forEach((cat: any) => {
                        const cid = String(cat.category_id || cat.categoryId || cat.id);
                        const cname = String(cat.category_name || cat.categoryName || cat.name || cid);
                        map[cid] = cname;
                        options.push({
                            label: `${cname} (${cid})`,
                            value: cid
                        });
                    });
                } else if (typeof res.data.data === 'object') {
                    Object.entries(res.data.data).forEach(([catId, catName]) => {
                        const cid = String(catId);
                        const cname = String(catName);
                        map[cid] = cname;
                        options.push({
                            label: `${cname} (${cid})`,
                            value: cid
                        });
                    });
                }

                options.sort((a, b) => a.label.localeCompare(b.label));
                setCategoryMap(map);
                setCategories(options);
            }
        } catch (err) {
            console.error('Failed to load categories', err);
        }
    };

    const loadRuleDetail = async (ruleId: string) => {
        setLoading(true);
        try {
            const res = await getShippingRuleDetailService(ruleId);
            const detail = res?.data?.data || location.state?.record;
            if (detail) {
                if (detail.shippingAttr) {
                    setPriority(detail.shippingAttr.priority || 1);
                    setActive(detail.shippingAttr.active ?? true);
                    if (detail.shippingAttr.date_active) setDateActive(moment(detail.shippingAttr.date_active));
                    if (detail.shippingAttr.date_expiry) setDateExpiry(moment(detail.shippingAttr.date_expiry));
                }
                if (detail.ruleCriteriaList && Array.isArray(detail.ruleCriteriaList)) {
                    setRuleCriteriaList(detail.ruleCriteriaList);
                }
            }
        } catch (err) {
            toast.error('Failed to load rule details');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCriteria = () => {
        setRuleCriteriaList(prev => [
            ...prev,
            {
                priority: prev.length + 1,
                evaluationType: 'ANY_OF',
                ruleList: [
                    {
                        conditionsList: [
                            {
                                evaluationType: 'ANY_OF',
                                criteriaList: [
                                    {
                                        categoryType: 'CATEGORY',
                                        attributeList: [{ categoryType: 'CATEGORY', attributeType: 'CATEGORY_ID', attributeValues: [''] }]
                                    }
                                ]
                            }
                        ],
                        actionList: {
                            criteriaList: [
                                {
                                    categoryType: 'SHIPPING',
                                    attributeList: [{ categoryType: 'SHIPPING', attributeType: 'SPECIAL_PRODUCT_SHIPPING_CHARGE', attributeValues: ['0'] }]
                                }
                            ]
                        }
                    }
                ]
            }
        ]);
    };

    const handleRemoveCriteria = (index: number) => {
        if (ruleCriteriaList.length <= 1) {
            toast.error('At least one rule criteria block is required.');
            return;
        }
        setRuleCriteriaList(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleAddCondition = (criteriaIndex: number) => {
        setRuleCriteriaList(prev => {
            const updated = [...prev];
            const currentRuleList = updated[criteriaIndex].ruleList || [];
            currentRuleList.push({
                conditionsList: [
                    {
                        evaluationType: 'ANY_OF',
                        criteriaList: [
                            {
                                categoryType: 'PRODUCT',
                                attributeList: [{ categoryType: 'PRODUCT', attributeType: 'PRODUCT_ID', attributeValues: [''] }]
                            }
                        ]
                    }
                ],
                actionList: {
                    criteriaList: [
                        {
                            categoryType: 'SHIPPING',
                            attributeList: [{ categoryType: 'SHIPPING', attributeType: 'SPECIAL_PRODUCT_SHIPPING_CHARGE', attributeValues: ['0'] }]
                        }
                    ]
                }
            });
            updated[criteriaIndex].ruleList = currentRuleList;
            return updated;
        });
    };

    const handleRemoveCondition = (criteriaIndex: number, conditionIndex: number) => {
        setRuleCriteriaList(prev => {
            const updated = [...prev];
            if (updated[criteriaIndex].ruleList.length <= 1) {
                toast.error('At least one condition rule is required per criteria block.');
                return prev;
            }
            updated[criteriaIndex].ruleList = updated[criteriaIndex].ruleList.filter((_, idx) => idx !== conditionIndex);
            return updated;
        });
    };

    const handleCriteriaTypeChange = (criteriaIndex: number, conditionIndex: number, newCategoryType: string) => {
        setRuleCriteriaList(prev => {
            const updated = [...prev];
            const cond = updated[criteriaIndex].ruleList[conditionIndex];
            if (cond.conditionsList?.[0]) {
                cond.conditionsList[0].criteriaList = [
                    {
                        categoryType: newCategoryType,
                        attributeList: [
                            {
                                categoryType: newCategoryType,
                                attributeType: newCategoryType === 'PRODUCT' ? 'PRODUCT_ID' :
                                    newCategoryType === 'CATEGORY' ? 'CATEGORY_ID' :
                                    newCategoryType === 'BRAND' ? 'BRAND_ID' : 'MIN_CART_VALUE',
                                attributeValues: ['']
                            }
                        ]
                    }
                ];
            }
            return updated;
        });
    };

    const handleConditionAttributeValueChange = (criteriaIndex: number, conditionIndex: number, val: any) => {
        setRuleCriteriaList(prev => {
            const updated = [...prev];
            const cond = updated[criteriaIndex].ruleList[conditionIndex];
            if (cond.conditionsList?.[0]?.criteriaList?.[0]?.attributeList?.[0]) {
                const attr = cond.conditionsList[0].criteriaList[0].attributeList[0];
                const arr = Array.isArray(val)
                    ? val
                    : (typeof val === 'string' && val ? val.split(',').map(s => s.trim()) : []);
                attr.attributeValues = arr;
                attr.attributeValue = arr.join(',');
            }
            return updated;
        });
    };

    const handleActionValueChange = (criteriaIndex: number, conditionIndex: number, val: string) => {
        setRuleCriteriaList(prev => {
            const updated = [...prev];
            const cond = updated[criteriaIndex].ruleList[conditionIndex];
            
            // Accommodate array format (legacy) or object format (new)
            const criteriaList = Array.isArray(cond.actionList) 
                ? cond.actionList 
                : cond.actionList?.criteriaList;
                
            if (criteriaList?.[0]?.attributeList?.[0]) {
                const attr = criteriaList[0].attributeList[0];
                attr.attributeValues = [val];
                attr.attributeValue = val;
            }
            return updated;
        });
    };

    const handleSubmit = async () => {
        const payload: ShippingRulePayload = {
            shippingAttr: {
                ...(id ? { shippingCriteriaId: Number(id) } : {}),
                priority: priority || 1,
                active,
                date_active: dateActive ? dateActive.toISOString() : moment().toISOString(),
                date_expiry: dateExpiry ? dateExpiry.toISOString() : moment().add(1, 'year').toISOString()
            },
            ruleCriteriaList
        };

        setSubmitting(true);
        try {
            let res: any;
            if (isUpdate) {
                res = await updateShippingCriteriaService(payload);
            } else {
                res = await createShippingCriteriaService(payload);
            }

            if (res?.data?.status || res?.status === 200) {
                toast.success(`Shipping rule ${isUpdate ? 'updated' : 'created'} successfully!`);
                navigate('/shipping-portal');
            } else {
                toast.error(res?.data?.statusDescription || res?.data?.message || 'Operation failed.');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Error saving shipping rule.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container p-4">
                    <div className="flex items-center justify-between w-full mb-4">
                        <Space>
                            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/shipping-portal')}>
                                Back to Rules
                            </Button>
                            <span className="text-xl font-bold text-[#43425D]">
                                {isUpdate
                                    ? `EDIT SHIPPING RULE ${id ? `(${categoryMap[id] ? `${categoryMap[id]} - ID: ${id}` : `#${id}`})` : ''}`
                                    : 'CREATE NEW SHIPPING RULE'}
                            </span>
                        </Space>
                        <Button
                            type="primary"
                            loading={submitting}
                            onClick={handleSubmit}
                            className="bg-blue-600 hover:bg-blue-700 h-9 px-6"
                        >
                            {isUpdate ? 'Update Shipping Rule' : 'Save Shipping Rule'}
                        </Button>
                    </div>

                    {loading ? (
                        <div className="p-8 text-center"><Spin size="large" /></div>
                    ) : (
                        <div className="flex flex-col gap-6">
                            {/* Top Level Rule Attributes */}
                            <Card title="Top-Level Shipping Rule Attributes" className="shadow-sm">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Priority *</label>
                                        <InputNumber
                                            min={1}
                                            value={priority}
                                            onChange={val => setPriority(val || 1)}
                                            className="w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Status Active</label>
                                        <div className="pt-1">
                                            <Switch
                                                checked={active}
                                                onChange={checked => setActive(checked)}
                                                checkedChildren="Active"
                                                unCheckedChildren="Inactive"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Date Active</label>
                                        <DatePicker
                                            value={dateActive}
                                            onChange={val => setDateActive(val)}
                                            className="w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Date Expiry</label>
                                        <DatePicker
                                            value={dateExpiry}
                                            onChange={val => setDateExpiry(val)}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </Card>

                            {/* Rule Criteria Blocks */}
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-md font-bold text-gray-700">Rule Evaluation Criteria Blocks</h3>
                                    <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddCriteria}>
                                        Add Criteria Block
                                    </Button>
                                </div>

                                {ruleCriteriaList.map((criteriaBlock, cIdx) => (
                                    <Card
                                        key={cIdx}
                                        title={`Criteria Block #${cIdx + 1}`}
                                        extra={
                                            <Button
                                                danger
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleRemoveCriteria(cIdx)}
                                            >
                                                Remove Block
                                            </Button>
                                        }
                                        className="border border-blue-100 shadow-sm"
                                    >
                                        <div className="flex items-center gap-4 mb-4 bg-gray-50 p-3 rounded">
                                            <div className="w-48">
                                                <label className="block text-xs font-semibold text-gray-600 mb-1">Evaluation Logic</label>
                                                <Select
                                                    value={criteriaBlock.evaluationType}
                                                    onChange={val => {
                                                        const updated = [...ruleCriteriaList];
                                                        updated[cIdx].evaluationType = val;
                                                        setRuleCriteriaList(updated);
                                                    }}
                                                    className="w-full"
                                                >
                                                    <Select.Option value="ANY_OF">ANY_OF (Match Any / OR)</Select.Option>
                                                    <Select.Option value="ALL_OF">ALL_OF (Match All / AND)</Select.Option>
                                                </Select>
                                            </div>
                                            <div className="w-32">
                                                <label className="block text-xs font-semibold text-gray-600 mb-1">Block Priority</label>
                                                <InputNumber
                                                    min={1}
                                                    value={criteriaBlock.priority}
                                                    onChange={val => {
                                                        const updated = [...ruleCriteriaList];
                                                        updated[cIdx].priority = val || 1;
                                                        setRuleCriteriaList(updated);
                                                    }}
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>

                                        <Divider className="my-3" />

                                        {/* Conditions List in Criteria Block */}
                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-gray-600">Conditions & Shipping Charges</span>
                                                <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => handleAddCondition(cIdx)}>
                                                    Add Condition Rule
                                                </Button>
                                            </div>

                                            {criteriaBlock.ruleList.map((condRule, rIdx) => {
                                                const currentCriteria = condRule.conditionsList?.[0]?.criteriaList?.[0];
                                                const catType = currentCriteria?.categoryType || 'CATEGORY';
                                                
                                                const attrValArray = currentCriteria?.attributeList?.[0]?.attributeValues;
                                                const attrVal = attrValArray && attrValArray.length > 0 
                                                    ? attrValArray.join(',') 
                                                    : (currentCriteria?.attributeList?.[0]?.attributeValue || '');
                                                    
                                                const actionListCriteria = Array.isArray(condRule.actionList) 
                                                    ? condRule.actionList[0] 
                                                    : condRule.actionList?.criteriaList?.[0];
                                                    
                                                const actionAttr = actionListCriteria?.attributeList?.find((a: any) => 
                                                    a.attributeType === 'SPECIAL_PRODUCT_SHIPPING_CHARGE' || a.attributeType === 'FIXED'
                                                ) || actionListCriteria?.attributeList?.[0];
                                                
                                                const actionVal = actionAttr?.attributeValues?.[0] || actionAttr?.attributeValue || '0';

                                                return (
                                                    <div key={rIdx} className="border p-3 rounded flex flex-wrap items-center gap-3 bg-white">
                                                        <div className="w-40">
                                                            <label className="block text-[11px] text-gray-500 mb-1">Category Type</label>
                                                            <Select
                                                                value={catType}
                                                                onChange={val => handleCriteriaTypeChange(cIdx, rIdx, val)}
                                                                className="w-full"
                                                            >
                                                                <Select.Option value="CATEGORY">CATEGORY</Select.Option>
                                                                <Select.Option value="PRODUCT">PRODUCT (MSNs)</Select.Option>
                                                                <Select.Option value="BRAND">BRAND</Select.Option>
                                                                <Select.Option value="CART">CART VALUE</Select.Option>
                                                            </Select>
                                                        </div>

                                                        <div className="flex-1 min-w-[200px]">
                                                            <label className="block text-[11px] text-gray-500 mb-1">Condition Value</label>
                                                            {catType === 'CATEGORY' ? (
                                                                <Select
                                                                    mode="multiple"
                                                                    showSearch
                                                                    placeholder="Search & Select Categories"
                                                                    options={categories}
                                                                    filterOption={(input, option) =>
                                                                        ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                                                                    }
                                                                    value={
                                                                        attrValArray && attrValArray.length > 0 && attrValArray[0] !== ''
                                                                            ? attrValArray
                                                                            : (attrVal ? attrVal.split(',').map(s => s.trim()).filter(Boolean) : [])
                                                                    }
                                                                    onChange={vals => handleConditionAttributeValueChange(cIdx, rIdx, vals)}
                                                                    className="w-full"
                                                                />
                                                            ) : (
                                                                <Input
                                                                    placeholder={
                                                                        catType === 'PRODUCT' ? 'Comma separated MSNs (e.g. MSN123, MSN456)' :
                                                                        catType === 'BRAND' ? 'Enter Brand IDs' :
                                                                        'Enter Cart Value range (e.g. 500-1000)'
                                                                    }
                                                                    value={attrVal}
                                                                    onChange={e => handleConditionAttributeValueChange(cIdx, rIdx, e.target.value)}
                                                                />
                                                            )}
                                                        </div>

                                                        <div className="w-44">
                                                            <label className="block text-[11px] text-gray-500 mb-1">Shipping Charge (₹)</label>
                                                            <Input
                                                                type="number"
                                                                placeholder="Charge amount (e.g. 50)"
                                                                value={actionVal}
                                                                onChange={e => handleActionValueChange(cIdx, rIdx, e.target.value)}
                                                            />
                                                        </div>

                                                        <Button
                                                            danger
                                                            type="text"
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => handleRemoveCondition(cIdx, rIdx)}
                                                            className="mt-4"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShippingRuleFormPage;
